/**
 * Transcription Queue Page
 *
 * Shows reports pending transcription (DRAFT, TRANSCRIBING)
 * For TRANSCRIPTOR role users
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mic,
  Play,
  Loader2,
  AlertCircle,
  FileText,
  Clock,
  CheckCircle,
} from 'lucide-react'

import {
  getTranscriptionQueue,
  startTranscription,
  getReportStatusVariant,
  formatReportDateTime,
} from '@/application/reports'
import { useStudy } from '@/application/studies'
import { APP_ROUTES, REPORT_STATUS_LABELS } from '@/config/constants'
import type { Report } from '@/domain/report'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { aFechaSegura } from '@/lib/utils'

// =============================================================================
// Queue Item Row Component
// =============================================================================

function QueueItemRow({
  report,
  onStartTranscription,
  isStarting,
}: {
  report: Report
  onStartTranscription: (id: string) => void
  isStarting: boolean
}) {
  const { data: study, isLoading: studyLoading } = useStudy(report.studyId)
  const navigate = useNavigate()

  const handleStartOrContinue = () => {
    if (report.status === 'DRAFT') {
      // Start transcription first, then navigate
      onStartTranscription(report.id)
    } else {
      // Already in TRANSCRIBING, navigate directly
      navigate(APP_ROUTES.TRANSCRIPTION.WORK(report.id))
    }
  }

  return (
    <tr className="border-b border-[var(--border)] hover:bg-[var(--background-subtle)] transition-colors">
      <td className="p-4">
        <Badge variant={getReportStatusVariant(report.status)}>
          {REPORT_STATUS_LABELS[report.status]}
        </Badge>
      </td>
      <td className="p-4">
        <div>
          <Link
            to={`/reports/${report.id}`}
            className="font-medium text-[var(--primary)] hover:underline"
          >
            {report.title}
          </Link>
          <p className="text-xs text-[var(--foreground-muted)]">
            Versión {report.version}
          </p>
        </div>
      </td>
      <td className="p-4">
        {studyLoading ? (
          <span className="text-[var(--foreground-muted)]">Cargando...</span>
        ) : study ? (
          <div>
            <Link
              to={`/studies/${study.id}`}
              className="text-sm text-[var(--foreground)] hover:underline"
            >
              {study.studyDescription || 'Sin descripción'}
            </Link>
            <p className="text-xs text-[var(--foreground-muted)]">
              {study.modality} • {aFechaSegura(study.studyDate).toLocaleDateString('es-CO')}
            </p>
          </div>
        ) : (
          <span className="text-[var(--foreground-muted)]">-</span>
        )}
      </td>
      <td className="p-4 text-sm text-[var(--foreground-muted)]">
        {formatReportDateTime(report.createdAt)}
      </td>
      <td className="p-4 text-right">
        <Button
          variant={report.status === 'DRAFT' ? 'default' : 'outline'}
          size="sm"
          onClick={handleStartOrContinue}
          disabled={isStarting}
        >
          {isStarting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : report.status === 'DRAFT' ? (
            <Play className="h-4 w-4 mr-2" />
          ) : (
            <Mic className="h-4 w-4 mr-2" />
          )}
          {report.status === 'DRAFT' ? 'Iniciar' : 'Continuar'}
        </Button>
      </td>
    </tr>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function TranscriptionQueuePage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [startingId, setStartingId] = useState<string | null>(null)

  // Query for transcription queue
  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['transcription-queue'],
    queryFn: getTranscriptionQueue,
  })

  // Mutation for starting transcription
  const startMutation = useMutation({
    mutationFn: (id: string) => startTranscription(id),
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['transcription-queue'] })
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      // Navigate to work page after starting
      navigate(APP_ROUTES.TRANSCRIPTION.WORK(report.id))
    },
    onSettled: () => {
      setStartingId(null)
    },
  })

  const handleStartTranscription = (id: string) => {
    setStartingId(id)
    startMutation.mutate(id)
  }

  const reports = data?.reports || []

  /**
   * Una cola vacia tiene dos causas muy distintas, y conviene decir cual es.
   *
   * O no hay trabajo hoy, que se espera; o a este transcriptor no le han
   * asignado ningun radiologo, que se resuelve hablando con el administrador.
   * Enseñar el mismo mensaje en los dos casos genera una llamada de soporte
   * en la que nadie sabe por donde empezar.
   */
  const sinRadiologosAsignados = (data as { radiologosAsignados?: number } | undefined)
    ?.radiologosAsignados === 0
  const draftCount = reports.filter((r) => r.status === 'DRAFT').length
  const transcribingCount = reports.filter((r) => r.status === 'TRANSCRIBING').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Cola de Transcripción</h1>
          <p className="text-[var(--foreground-muted)]">
            Informes pendientes de transcripción
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-900/40 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Nuevos</p>
                <p className="text-xl font-semibold">{draftCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">En Progreso</p>
                <p className="text-xl font-semibold">{transcribingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Total</p>
                <p className="text-xl font-semibold">{reports.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Informes Pendientes
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-500">{error?.message || 'Error al cargar cola'}</p>
            </div>
          ) : !reports.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Mic className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
              {sinRadiologosAsignados ? (
                <>
                  <p className="text-[var(--foreground)]">
                    Todavía no tiene radiólogos asignados
                  </p>
                  <p className="mt-1 max-w-md text-sm text-[var(--foreground-muted)]">
                    Su cola muestra los informes de los especialistas que le asignen.
                    Pídale a su administrador que le asigne los suyos, en Usuarios.
                  </p>
                </>
              ) : (
                <p className="text-[var(--foreground-muted)]">
                  No hay informes pendientes de transcripción
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--background-subtle)]">
                    <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                      Estado
                    </th>
                    <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                      Informe
                    </th>
                    <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                      Estudio
                    </th>
                    <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                      Creado
                    </th>
                    <th className="text-right p-4 font-medium text-[var(--foreground-muted)]">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <QueueItemRow
                      key={report.id}
                      report={report}
                      onStartTranscription={handleStartTranscription}
                      isStarting={startingId === report.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
